import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sparkles,
  Thermometer,
  Star,
  Lightbulb,
  MessageSquare,
  ClipboardList,
  ShieldAlert,
  ThumbsUp,
  User,
  Inbox,
} from "lucide-react";

type QualificationStatus = "answered" | "partial" | "unanswered";

interface QualificationQA {
  question: string;
  answer: string;
  status: QualificationStatus;
}

interface ObjectionItem {
  objection: string;
  how_it_was_handled: string;
  suggested_response_human: string;
  suggested_improvement_initial_stage: string;
}

interface ImprovementAreas {
  human: string[];
  initial_stage: string[];
}

interface SummaryData {
  summary: string;
  lead_temperature: "frio" | "morno" | "quente";
  temperature_reason: string;
  quality_score: number;
  quality_insights: string;
  improvement_tips: string[];
  message_count: number;
  qualification_qa?: QualificationQA[];
  objections?: ObjectionItem[];
  strengths?: string[];
  improvement_areas?: ImprovementAreas;
}

interface ConversationSummarySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: SummaryData | null;
  isLoading: boolean;
}

const temperatureConfig = {
  frio: { label: "Frio", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300", emoji: "🧊" },
  morno: { label: "Morno", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300", emoji: "🔥" },
  quente: { label: "Quente", color: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300", emoji: "🔥🔥" },
};

const qualificationStatusConfig: Record<
  QualificationStatus,
  { label: string; color: string }
> = {
  answered: {
    label: "Respondida",
    color: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  },
  partial: {
    label: "Parcial",
    color: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  },
  unanswered: {
    label: "Não respondida",
    color: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  },
};

function ScoreBar({ score }: { score: number }) {
  const percentage = (score / 10) * 100;
  const color =
    score >= 8 ? "bg-green-500" : score >= 5 ? "bg-amber-500" : "bg-red-500";

  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-2.5 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${color}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-lg font-bold text-foreground min-w-[2.5rem] text-right">
        {score}/10
      </span>
    </div>
  );
}

function SectionTitle({
  icon: Icon,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground uppercase tracking-wide">
      <Icon className="w-4 h-4" />
      {children}
    </div>
  );
}

export function ConversationSummarySheet({
  open,
  onOpenChange,
  data,
  isLoading,
}: ConversationSummarySheetProps) {
  const qualificationQa = data?.qualification_qa ?? [];
  const objections = data?.objections ?? [];
  const strengths = data?.strengths ?? [];
  const improvementAreas = data?.improvement_areas;
  const hasHumanImprovements = (improvementAreas?.human?.length ?? 0) > 0;
  const hasInitialStageImprovements = (improvementAreas?.initial_stage?.length ?? 0) > 0;
  const hasLegacyTips = (data?.improvement_tips?.length ?? 0) > 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-0">
        <SheetHeader className="p-6 pb-4 border-b bg-muted/30">
          <SheetTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-primary" />
            Resumo do Atendimento
          </SheetTitle>
        </SheetHeader>

        <div className="p-6 space-y-6">
          {isLoading ? (
            <div className="space-y-6">
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-20 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-10 w-28" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-44" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            </div>
          ) : data ? (
            <>
              {/* Summary */}
              <section className="space-y-2">
                <SectionTitle icon={MessageSquare}>Resumo</SectionTitle>
                <p className="text-sm leading-relaxed text-foreground bg-muted/40 rounded-lg p-4">
                  {data.summary}
                </p>
                <p className="text-xs text-muted-foreground">
                  {data.message_count} mensagens analisadas
                </p>
              </section>

              {/* Temperature */}
              <section className="space-y-2">
                <SectionTitle icon={Thermometer}>Temperatura do Lead</SectionTitle>
                <div className="flex items-center gap-3">
                  <Badge
                    className={`text-sm px-3 py-1.5 font-semibold ${
                      temperatureConfig[data.lead_temperature].color
                    }`}
                  >
                    {temperatureConfig[data.lead_temperature].emoji}{" "}
                    {temperatureConfig[data.lead_temperature].label}
                  </Badge>
                </div>
                {data.temperature_reason && (
                  <p className="text-sm text-muted-foreground italic">
                    {data.temperature_reason}
                  </p>
                )}
              </section>

              {/* Qualification */}
              {qualificationQa.length > 0 && (
                <section className="space-y-3">
                  <SectionTitle icon={ClipboardList}>Qualificação</SectionTitle>
                  <ul className="space-y-3">
                    {qualificationQa.map((item, i) => (
                      <li
                        key={i}
                        className="text-sm text-foreground bg-muted/40 rounded-lg p-3 space-y-2"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-medium leading-snug">{item.question}</p>
                          <Badge
                            className={`shrink-0 text-xs ${
                              qualificationStatusConfig[item.status].color
                            }`}
                          >
                            {qualificationStatusConfig[item.status].label}
                          </Badge>
                        </div>
                        {item.answer && (
                          <p className="text-muted-foreground leading-relaxed">
                            {item.answer}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Quality */}
              <section className="space-y-3">
                <SectionTitle icon={Star}>Qualidade do Atendimento</SectionTitle>
                <ScoreBar score={data.quality_score} />
                {data.quality_insights && (
                  <p className="text-sm leading-relaxed text-foreground bg-muted/40 rounded-lg p-4">
                    {data.quality_insights}
                  </p>
                )}
              </section>

              {/* Strengths */}
              {strengths.length > 0 && (
                <section className="space-y-3">
                  <SectionTitle icon={ThumbsUp}>Pontos Fortes</SectionTitle>
                  <ul className="space-y-2">
                    {strengths.map((strength, i) => (
                      <li
                        key={i}
                        className="flex gap-3 text-sm text-foreground bg-green-50 dark:bg-green-950/20 rounded-lg p-3"
                      >
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 flex items-center justify-center text-xs font-bold">
                          {i + 1}
                        </span>
                        <span className="leading-relaxed">{strength}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Objections */}
              {objections.length > 0 && (
                <section className="space-y-3">
                  <SectionTitle icon={ShieldAlert}>Objeções e Contrapontos</SectionTitle>
                  <ul className="space-y-3">
                    {objections.map((item, i) => (
                      <li
                        key={i}
                        className="text-sm text-foreground bg-muted/40 rounded-lg p-4 space-y-3"
                      >
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                            Objeção
                          </p>
                          <p className="leading-relaxed font-medium">{item.objection}</p>
                        </div>
                        {item.how_it_was_handled && (
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                              Como foi tratada
                            </p>
                            <p className="leading-relaxed text-muted-foreground">
                              {item.how_it_was_handled}
                            </p>
                          </div>
                        )}
                        {item.suggested_response_human && (
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
                              <User className="w-3 h-3" />
                              Sugestão para atendente humano
                            </p>
                            <p className="leading-relaxed">{item.suggested_response_human}</p>
                          </div>
                        )}
                        {item.suggested_improvement_initial_stage && (
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
                              <Inbox className="w-3 h-3" />
                              Sugestão para etapa inicial
                            </p>
                            <p className="leading-relaxed">{item.suggested_improvement_initial_stage}</p>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Improvement Areas (human vs initial stage) */}
              {(hasHumanImprovements || hasInitialStageImprovements) && (
                <section className="space-y-4">
                  <SectionTitle icon={Lightbulb}>Áreas de Melhoria</SectionTitle>

                  {hasHumanImprovements && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                        <User className="w-3 h-3" />
                        Atendente humano
                      </p>
                      <ul className="space-y-2">
                        {improvementAreas!.human.map((tip, i) => (
                          <li
                            key={i}
                            className="flex gap-3 text-sm text-foreground bg-muted/40 rounded-lg p-3"
                          >
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                              {i + 1}
                            </span>
                            <span className="leading-relaxed">{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {hasInitialStageImprovements && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                        <Inbox className="w-3 h-3" />
                        Etapa inicial
                      </p>
                      <ul className="space-y-2">
                        {improvementAreas!.initial_stage.map((tip, i) => (
                          <li
                            key={i}
                            className="flex gap-3 text-sm text-foreground bg-muted/40 rounded-lg p-3"
                          >
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                              {i + 1}
                            </span>
                            <span className="leading-relaxed">{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </section>
              )}

              {/* Legacy Improvement Tips */}
              {hasLegacyTips && (
                <section className="space-y-3">
                  <SectionTitle icon={Lightbulb}>Sugestões de Melhoria</SectionTitle>
                  <ul className="space-y-2">
                    {data.improvement_tips.map((tip, i) => (
                      <li
                        key={i}
                        className="flex gap-3 text-sm text-foreground bg-muted/40 rounded-lg p-3"
                      >
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                          {i + 1}
                        </span>
                        <span className="leading-relaxed">{tip}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
